import { useGetList, useListContext, useNotify, useUpdate } from "ra-core";
import { Button } from "@/components/ui/button";

import { Note } from "../notes/Note";
import type {
  Contact,
  ContactNote,
  LeadEvent,
  LibraryFile,
  Message,
  Task,
} from "../types";

const trackingLabels = (message: Message) => {
  if (message.status === "cancelled") return ["Cancelled"];
  if (message.status === "scheduled" && message.scheduled_at) {
    return [`Scheduled for ${new Date(message.scheduled_at).toLocaleString()}`];
  }
  if (message.status === "failed") return ["Failed"];
  const labels: string[] = [];
  if (
    message.sent_at ||
    ["sent", "delivered", "opened", "clicked"].includes(message.status)
  ) {
    labels.push("Sent");
  }
  if (message.delivered_at || message.status === "delivered") {
    labels.push("Delivered");
  }
  if (message.opened_at) labels.push("Opened");
  if (message.clicked_at) labels.push("Clicked");
  return labels;
};

export const CustomerTimeline = ({ contact }: { contact: Contact }) => {
  const notify = useNotify();
  const [update] = useUpdate();
  const { data: notes = [], isPending: notesPending } =
    useListContext<ContactNote>();
  const { data: messages = [] } = useGetList<Message>("messages", {
    filter: { contact_id: contact.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });
  const { data: tasks = [] } = useGetList<Task>("tasks", {
    filter: { contact_id: contact.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "due_date", order: "DESC" },
  });
  const { data: files = [] } = useGetList<LibraryFile>("library_files", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: events = [] } = useGetList<LeadEvent>("lead_events", {
    filter: { contact_id: contact.id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });

  if (notesPending) return null;

  const items = [
    ...notes.map((note) => ({
      at: note.date,
      key: `note-${note.id}`,
      kind: "note" as const,
      note,
    })),
    ...messages.map((message) => ({
      at: message.created_at,
      key: `message-${message.id}`,
      kind: "message" as const,
      message,
    })),
    ...tasks.map((task) => ({
      at: task.due_date,
      key: `task-${task.id}`,
      kind: "task" as const,
      task,
    })),
    ...events.map((event) => ({
      at: event.created_at,
      key: `event-${event.id}`,
      kind: "event" as const,
      event,
    })),
  ].sort((a, b) => new Date(b.at).valueOf() - new Date(a.at).valueOf());

  if (!items.length) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">No activity yet.</p>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      {items.map((item) => {
        if (item.kind === "note") {
          return <Note key={item.key} note={item.note} isLast />;
        }
        if (item.kind === "message") {
          const message = item.message;
          const tracking = trackingLabels(message);
          return (
            <article key={item.key} className="text-sm">
              <p className="text-muted-foreground">
                {message.direction === "inbound" ? "Received" : "Sent"}{" "}
                {message.channel === "sms" ? "text" : "email"}
                {tracking.length ? ` · ${tracking.join(" · ")}` : ""}
                {message.provider ? ` · ${message.provider}` : ""}
              </p>
              {message.subject ? (
                <p className="font-medium">{message.subject}</p>
              ) : null}
              {message.provider === "gmail" && message.to_address ? (
                <p className="text-muted-foreground">To {message.to_address}</p>
              ) : null}
              <p className="whitespace-pre-wrap">{message.body}</p>
              {message.file_id ? (
                <p className="text-muted-foreground">
                  File:{" "}
                  {files.find((file) => file.id === message.file_id)?.name ??
                    "Attached file"}
                </p>
              ) : null}
              {message.status === "scheduled" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-7 px-2"
                  onClick={() =>
                    update(
                      "messages",
                      {
                        id: message.id,
                        data: { status: "cancelled" },
                        previousData: message,
                      },
                      {
                        onSuccess: () => notify("Scheduled message cancelled"),
                      },
                    )
                  }
                >
                  Cancel
                </Button>
              ) : null}
            </article>
          );
        }
        if (item.kind === "task") {
          return (
            <article key={item.key} className="text-sm">
              <p className="text-muted-foreground">
                Reminder{item.task.done_date ? " · done" : ""}
              </p>
              <p>{item.task.text}</p>
            </article>
          );
        }
        return (
          <article key={item.key} className="text-sm text-muted-foreground">
            {item.event.body}
          </article>
        );
      })}
    </div>
  );
};
